'use client'

import { Stack, Switch, styled, switchClasses } from '@mui/material'
import Typography from '@mui/material/Typography'
import { ChangeEvent, ReactElement, ReactNode } from 'react'
import { tss } from 'tss-react/mui'

const useStyles = tss.create(() => ({
    semibold: {
        fontWeight: 600,
    },
    stack: {
        alignItems: 'center',
    },
}))

export type AntSwitchProps = {
    checked: boolean
    label: string
    secondaryLabel: string
    onChange: (checked: boolean) => void
}

const StyledSwitch = styled(Switch)(({ theme }) => ({
    [`& .${switchClasses.switchBase}`]: {
        height: 38,
        color: theme.palette.primary.main,
        '&.Mui-checked + .MuiSwitch-track': {
            backgroundColor: theme.palette.primary.light,
            opacity: 0.38,
        },
    },
    [`& .${switchClasses.track}`]: {
        backgroundColor: theme.palette.primary.light,
    },
}))

function AntSwitch({ checked, label, secondaryLabel, onChange }: AntSwitchProps): ReactElement {
    const { classes, cx } = useStyles()
    const onCaptionClick = () => {
        onChange(!checked)
    }
    const handleOnChange = (_event: ChangeEvent<HTMLInputElement>, nextCheckedValue: boolean) => {
        onChange(nextCheckedValue)
    }

    // Labels are not toggles: once clicked, the switch is set to the corresponding position
    // and the label is no longer clickable.
    const SwitchLabel = ({ addOnClick, children }: { addOnClick: boolean; children: ReactNode }) => (
        <Typography
            variant="caption"
            role="button"
            onClick={addOnClick ? onCaptionClick : undefined}
            className={classes.semibold}
        >
            {children}
        </Typography>
    )

    return (
        <Stack direction="row" className={cx(classes.stack, 'cursor-pointer')} sx={{}}>
            <SwitchLabel addOnClick={checked}>{label}</SwitchLabel>
            <StyledSwitch
                checked={checked}
                inputProps={{ 'aria-label': `${label} / ${secondaryLabel}` }}
                onChange={handleOnChange}
            />
            <SwitchLabel addOnClick={!checked}>{secondaryLabel}</SwitchLabel>
        </Stack>
    )
}

export default AntSwitch
